import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Text } from '@fragments';
import { IconPlus } from '@tabler/icons-react';
import { Thumb } from '.';
import { loadTemplate } from './useTemplateDocument';
import { modelSections } from './pageRoles';
import { enter } from '../view/motion';
import './styles.scss';

// The page models (contracts 2.10.0 and 2.11.0): the static kit's one-page templates
// (`category_key: static`), offered when the creator adds a page to a carousel, grouped by
// where each fits (`page_roles`): covers, content pages and closings. A model that fits more
// than one place is in each group; the group of the place the page goes (`place`) comes
// first. "Página em branco" comes before them. `source` is the host's gallery:
// `templates({ format, category_key })` and `template(id)` (with its document). Picking
// resolves the model's document for `onPick`; the blank page calls `onBlank`. `ratio` is the
// art's (`width / height`), for the blank page's card.
export const PAGE_MODELS_CATEGORY = 'static';

export default function PageModels({ open, onClose, source, onPick, onBlank, place = 'content', ratio = '1080 / 1350' }) {
  const { t } = useTranslation('artEditor');
  const [state, setState] = useState({ status: 'loading', models: [] });
  const [picking, setPicking] = useState(null);
  const [failed, setFailed] = useState(null);
  const body = useRef(null);

  // The dialog may close (or unmount) while a model's document loads: nothing lands then.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // The catalog's kit only: a past design or personal template of the category is not a model.
  const load = useCallback(async () => {
    setState({ status: 'loading', models: [] });
    try {
      const page = await source.templates({ format: 'instagram::post', category_key: PAGE_MODELS_CATEGORY, source: 'ginastee', per_page: 50 });
      if (!alive.current) return;
      setState({ status: 'ready', models: page.data.filter(item => item.category_key === PAGE_MODELS_CATEGORY && !['past_design', 'personal'].includes(item.source)) });
    } catch { if (alive.current) setState({ status: 'error', models: [] }); }
  }, [source]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useLayoutEffect(() => { if (open && state.status === 'ready') enter(body.current, { y: 8 }, { duration: 0.22 }); }, [open, state.status]);

  const pick = async model => {
    if (picking) return;
    setPicking(model.id);
    setFailed(null);
    try {
      const detail = model.document ? model : await loadTemplate(model, source.template);
      if (alive.current) await onPick({ ...model, ...detail });
    } catch { if (alive.current) setFailed(model.id); } finally { if (alive.current) setPicking(null); }
  };

  const card = model => <div key={model.id} role="listitem" className="art-gallery__item">
    <button type="button" className={`art-gallery__card ${picking === model.id ? 'is-selected' : ''}`} aria-label={model.name} disabled={!!picking} onClick={() => pick(model)}>
      <Thumb template={model} fetch={source.template} />
      <span className="art-gallery__card-text"><Text.span weight={500} contrast={9} className="art-gallery__card-name">{model.name}</Text.span></span>
    </button>
  </div>;

  return <Modal open={open} onClose={onClose} size={980} className="studio-dialog art-page-models-modal" closeLabel={t('close')}
    title={t('pageModelsTitle')} subtitle={t('pageModelsSubtitle')}>
    <div ref={body} className="art-gallery art-page-models" aria-busy={state.status === 'loading' || undefined}>
      {failed && <div className="art-inline-error" role="alert"><Text.caption contrast={8}>{t('templateLoadError')}</Text.caption></div>}
      {state.status === 'error' && <div className="art-inline-error" role="alert">
        <Text.caption contrast={8}>{t('galleryLoadError')}</Text.caption>
        <Button size="small" variant="text" color="overlay" preventLoading onClick={load}>{t('retry')}</Button>
      </div>}
      <div className="art-gallery__grid art-page-models__grid" role="list">
        <div role="listitem" className="art-gallery__item">
          <button type="button" className="art-gallery__card art-page-models__blank" disabled={!!picking} onClick={onBlank}>
            <span className="art-gallery__thumb art-page-models__blank-thumb" style={{ aspectRatio: ratio }}><IconPlus aria-hidden="true" /></span>
            <span className="art-gallery__card-text"><Text.span weight={500} contrast={9} className="art-gallery__card-name">{t('blankPage')}</Text.span>
              <Text.caption contrast={6}>{t('blankPageHint')}</Text.caption></span>
          </button>
        </div>
        {state.status === 'loading' && [0, 1, 2, 3, 4].map(index => <div key={index} role="listitem" className="art-gallery__item">
          <span className="art-gallery__card is-skeleton"><span className="art-gallery__thumb art-skeleton" style={{ aspectRatio: '4 / 5' }} /></span>
        </div>)}
      </div>
      {state.status === 'ready' && modelSections(state.models, place).map(section => <section key={section.role} className="art-gallery__section art-page-models__section"
        aria-labelledby={`art-page-models-${section.role}`}>
        <div className="art-gallery__section-head">
          <Text.h5 id={`art-page-models-${section.role}`} weight={500} contrast={9}>{t(`pageRole_${section.role}`)}</Text.h5>
          <Text.caption contrast={6}>{t(`pageRoleHint_${section.role}`)}</Text.caption>
        </div>
        <div className="art-gallery__grid art-page-models__grid" role="list">{section.models.map(card)}</div>
      </section>)}
    </div>
  </Modal>;
}
